/**
 * Navigation Guards (Shared)
 *
 * Handles:
 * - Authentication checks for protected routes
 * - Role-based access control
 * - Redirect logic for unauthenticated users
 */
import type { Router } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Route meta type augmentation
declare module 'vue-router' {
  interface RouteMeta {
    /** Whether the route requires authentication */
    requiresAuth?: boolean
    /** Required roles (any match grants access) */
    roles?: Array<'admin' | 'staff' | 'client'>
    /** Layout to use (for layout switching) */
    layout?: 'default' | 'auth' | 'minimal' | 'fullscreen'
    /** Page title for document.title */
    title?: string
  }
}

/**
 * Setup all navigation guards on the router
 */
export function setupGuards(router: Router): void {
  router.beforeEach(async (to) => {
    // DEBUG: Log IMMEDIATELY before any logic
    console.log('[Guard] ===== NAVIGATION START =====')
    console.log('[Guard] Target:', {
      path: to.path,
      fullPath: to.fullPath,
      name: to.name,
      params: to.params,
      matched: to.matched.map(r => ({ path: r.path, name: r.name }))
    })

    const authStore = useAuthStore()

    // Set page title
    const title = to.meta.title || 'ClaimShield'
    document.title = title

    // DEBUG: Log route meta details
    console.log('[Guard] Route meta:', {
      requiresAuth: to.meta.requiresAuth,
      layout: to.meta.layout,
      roles: to.meta.roles
    })

    // Public routes - no auth required
    if (to.meta.requiresAuth === false) {
      console.log('[Guard] ✅ Public route detected, allowing access')
      // If already logged in and going to login, redirect to dashboard
      if (to.name === 'login' && authStore.isAuthenticated) {
        return { name: 'dashboard' }
      }
      return true
    }

    console.log('[Guard] ⚠️ Route requires auth (requiresAuth not explicitly false)')

    // Routes without explicit requiresAuth=false require authentication
    // Check if authenticated
    if (!authStore.isAuthenticated) {
        console.log('[Guard] User not authenticated, checking initialization...')
        // Try to restore session first
        if (!authStore.initialized) {
          console.log('[Guard] Initializing auth store...')
          await authStore.initialize()
        }

        // Still not authenticated after init attempt
        if (!authStore.isAuthenticated) {
          console.log('[Guard] ❌ Still not authenticated, redirecting to login')
          // Save intended destination for redirect after login
          const redirectPath = to.fullPath !== '/login' ? to.fullPath : undefined

          return {
            name: 'login',
            query: redirectPath ? { redirect: redirectPath } : undefined,
          }
        }
      }

    // Check role requirements
    if (to.meta.roles && to.meta.roles.length > 0) {
      const userRole = authStore.user?.role
      if (!userRole || !to.meta.roles.includes(userRole)) {
        // User doesn't have required role
        console.warn(`[Guard] Access denied to ${to.path}. Required roles: ${to.meta.roles}`)
        return { name: 'dashboard' } // Redirect to dashboard instead of showing error
      }
    }

    return true
  })

  // Handle navigation errors
  router.onError((error, to) => {
    console.error('[Router] Navigation error:', error)

    // Handle chunk loading errors (usually due to new deployment)
    if (error.message.includes('Failed to fetch dynamically imported module')) {
      console.log('[Router] Chunk load failed, reloading page...')
      window.location.href = to.fullPath
    }
  })
}
